import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  type JitsiExternalApi,
  loadJitsiExternalApi,
} from "@/lib/jitsi";

interface JitsiMeetingProps {
  domain: string;
  roomName: string;
  jwt: string;
  displayName?: string;
  onLeave?: () => void;
}

// IFrame Jitsi pilote par `JitsiMeetExternalAPI`. Le JWT (signe HS256
// cote core avec JITSI_APP_SECRET) controle a la fois l'admission a la
// salle et le statut moderateur via `context.user.moderator`.
export function JitsiMeeting({
  domain,
  roomName,
  jwt,
  displayName,
  onLeave,
}: JitsiMeetingProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<JitsiExternalApi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    loadJitsiExternalApi(domain)
      .then(() => {
        if (cancelled) return;
        if (!containerRef.current || !window.JitsiMeetExternalAPI) {
          setError("API Jitsi indisponible.");
          setLoading(false);
          return;
        }

        const api = new window.JitsiMeetExternalAPI(domain, {
          roomName,
          jwt,
          parentNode: containerRef.current,
          width: "100%",
          height: "100%",
          userInfo: displayName ? { displayName } : undefined,
          configOverwrite: {
            // Sobriete : pas de prejoin, l'utilisateur a deja consenti.
            prejoinPageEnabled: false,
            disableDeepLinking: true,
          },
        });

        const handleReady = () => setLoading(false);
        const handleLeave = () => onLeave?.();
        api.addListener("videoConferenceJoined", handleReady);
        api.addListener("readyToClose", handleLeave);

        apiRef.current = api;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : "Echec du chargement de l'audience.",
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
      apiRef.current?.dispose();
      apiRef.current = null;
    };
  }, [domain, roomName, jwt, displayName, onLeave]);

  return (
    <div className="relative w-full h-full min-h-[480px]">
      {loading && !error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-sm text-white/70 z-10">
          <Loader2 className="animate-spin" size={28} />
          Connexion a la salle d'audience...
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-red-200 z-10">
          {error}
        </div>
      )}
      <div ref={containerRef} className="w-full h-full" />
    </div>
  );
}
